import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import ScheduleCallFAB from "@/components/ScheduleCallFAB";
import StartInvestingModal from "@/components/StartInvestingModal";
import { AuthProvider } from "@/context/AuthContext";
import PortfolioReviewPopup from "@/components/PortfolioReviewPopup";

const About = lazy(() => import("@/pages/About"));
const Home = lazy(() => import("@/pages/Home"));
const Services = lazy(() => import("@/pages/Services"));
const MutualFunds = lazy(() => import("@/pages/MutualFunds"));
const StethoWealth = lazy(() => import("@/pages/StethoWealth"));
const Calculators = lazy(() => import("@/pages/Calculators"));
const MutualFundReturns = lazy(() => import("@/pages/MutualFundReturns"));
const FundDetail = lazy(() => import("@/pages/FundDetail"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Partners = lazy(() => import("@/pages/Partners"));
const Contact = lazy(() => import("@/pages/Contact"));
const Admin = lazy(() => import("@/pages/Admin"));
const CommissionDisclosure = lazy(() => import("@/pages/CommissionDisclosure"));
const ChildrenEducation = lazy(() => import("@/pages/ChildrenEducation"));
const TaxPlanning = lazy(() => import("@/pages/TaxPlanning"));
const FDBonds = lazy(() => import("@/pages/FDBonds"));
const PMSSif = lazy(() => import("@/pages/PMSSif"));
const P2PLoans = lazy(() => import("@/pages/P2PLoans"));
const Careers = lazy(() => import("@/pages/Careers"));
const Insurance = lazy(() => import("@/pages/Insurance"));
const WealthVitals = lazy(() => import("@/pages/WealthVitals"));
const Terms = lazy(() => import("@/pages/Terms"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const FundSelectionPolicy = lazy(() => import("@/pages/FundSelectionPolicy"));
const GrievanceRedressal = lazy(() => import("@/pages/GrievanceRedressal"));
const ImportantLinks = lazy(() => import("@/pages/ImportantLinks"));
const RightsObligations = lazy(() => import("@/pages/RightsObligations"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const Campaign = lazy(() => import("@/pages/Campaign"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Layout({ children, onStartInvesting }) {
  return (
    <>
      <Navbar onStartInvesting={onStartInvesting} />
      <main>{children}</main>
      <Footer />
      <WhatsAppFAB />
      <ScheduleCallFAB />
    </>
  );
}

function GlobalPopups() {
  const { pathname } = useLocation();
  // Keep the campaign landing pages distraction-free for ad conversions
  if (pathname.startsWith("/campaign")) return null;
  return <PortfolioReviewPopup />;
}

function App() {
  const [startInvestingOpen, setStartInvestingOpen] = useState(false);
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <GlobalPopups />
        <StartInvestingModal open={startInvestingOpen} onClose={() => setStartInvestingOpen(false)} />
        <Suspense fallback={<div style={{minHeight:"100vh"}} />}>
        <Routes>
          <Route path="/about" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><About onStartInvesting={() => setStartInvestingOpen(true)} /></Layout>} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Home onStartInvesting={() => setStartInvestingOpen(true)} /></Layout>} />
          <Route path="/services" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Services /></Layout>} />
          <Route path="/mutual-funds" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><MutualFunds /></Layout>} />
          <Route path="/stethowealth" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><StethoWealth /></Layout>} />
          <Route path="/calculators" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Calculators /></Layout>} />
          <Route path="/mutual-fund-returns" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><MutualFundReturns /></Layout>} />
          <Route path="/mutual-fund/:schemeCode/:slug" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><FundDetail /></Layout>} />
          <Route path="/blog" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Blog /></Layout>} />
          <Route path="/blog/:slug" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><BlogPost /></Layout>} />
          <Route path="/partners" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Partners /></Layout>} />
          <Route path="/contact" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Contact /></Layout>} />
          <Route path="/commission-disclosure" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><CommissionDisclosure /></Layout>} />
          <Route path="/commission-disclosures" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><CommissionDisclosure /></Layout>} />
          <Route path="/children-education" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><ChildrenEducation /></Layout>} />
          <Route path="/tax-planning" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><TaxPlanning /></Layout>} />
          <Route path="/fd-bonds" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><FDBonds /></Layout>} />
          <Route path="/pms-sif" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><PMSSif /></Layout>} />
          <Route path="/p2p-loans" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><P2PLoans /></Layout>} />
          <Route path="/careers" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Careers /></Layout>} />
          <Route path="/wealthvitals" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><WealthVitals onStartInvesting={() => setStartInvestingOpen(true)} /></Layout>} />
          <Route path="/insurance" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Insurance /></Layout>} />
          <Route path="/terms" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Terms /></Layout>} />
          <Route path="/privacy-policy" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><PrivacyPolicy /></Layout>} />
          <Route path="/fund-selection-policy" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><FundSelectionPolicy /></Layout>} />
          <Route path="/grievance-redressal" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><GrievanceRedressal /></Layout>} />
          <Route path="/important-links" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><ImportantLinks /></Layout>} />
          <Route path="/rights-obligations" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><RightsObligations /></Layout>} />
          <Route path="/disclaimer" element={<Layout onStartInvesting={() => setStartInvestingOpen(true)}><Disclaimer /></Layout>} />
          <Route path="/campaigns" element={<Campaign />} />
          <Route path="/campaign" element={<Campaign />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
